import { newTableTheme } from '@ardoq/table-legacy';
import styled from 'styled-components';
import {
  GetComponentLinkHandler,
  SearchResultsTable,
  getAggregateLabels,
} from '@ardoq/report-reader';
import { Aggregate, ArdoqId, ReportColumnType } from '@ardoq/api-types';
import { toUsersDataForRenderersById } from '@ardoq/renderers';
import React from 'react';
import { noop } from 'lodash';
import { Maybe, pluralize } from '@ardoq/common-helpers';
import { Text, text1 } from '@ardoq/typography';
import { Locale } from '@ardoq/locale';
import { EnhancedScopeData } from '@ardoq/data-model';
import { colors, fontSize, lineHeight, s16, s4 } from '@ardoq/design-tokens';
import { type I18nLocale } from '@ardoq/i18n';

const Thead = styled(newTableTheme.Thead)`
  background-color: inherit;
  &::after {
    display: none;
  }
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 13px 16px;
  height: 100%;
`;

const Td = styled(newTableTheme.Td)`
  height: 20px;
  min-height: 20px;

  > div {
    display: flex;
    min-height: 20px;
  }

  &,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LightTd = styled(Td)`
  font-size: ${fontSize.regular};
  height: 24px;

  > div {
    justify-content: var(--justify, flex-start);
  }
`;

const Th = styled(newTableTheme.Th)`
  height: 22px;
  min-height: 22px;
  background: ${colors.white950};
  @media (min-width: 1440px) {
    height: 26px;
    min-height: 26px;
  }

  @media (min-width: 1512px) {
    height: 27px;
    min-height: 27px;
  }

  @media (min-width: 1600px) {
    height: 29px;
    min-height: 29px;
  }

  @media (min-width: 1728px) {
    height: 36px;
    min-height: 36px;
  }

  @media (min-width: 2560px) {
    height: 38px;
    min-height: 38px;
  }
`;

const LightTh = styled(Th)`
  font-weight: 500;
  font-size: 12px;
  color: ${colors.textModerate};
`;

const Table = newTableTheme.Table;

const FixedTable = styled(Table)`
  table-layout: fixed;
`;

const LightBodyTr = styled(newTableTheme.BodyTr)`
  td {
    ${text1};
    font-size: 14px;
    height: 24px;
    line-height: ${lineHeight.xlarge};
    padding: ${s4} ${s16} 0 ${s16};
  }

  td:first-child {
    padding-left: 0;
    font-weight: 500;
    font-size: 14px;
  }
  td:not(:first-child) {
    color: ${colors.textModerate};
  }
`;

const TotalNumberOfRows = styled(Text).attrs({
  variant: 'caption',
  color: 'textSubtle',
})`
  position: absolute;
  bottom: 5px;
  right: 10px;

  @media (max-width: 1280px) {
    bottom: 3px;
    font-size: 10px;
  }
`;

export type TableWidgetProps = {
  userLocale: Locale;
  i18nLocale: I18nLocale;
  hasComponentReadPermission: (componentId: ArdoqId) => boolean;
  scopeData: Maybe<EnhancedScopeData>;
  data: Array<Record<string, any>>;
  tableFields: Array<{
    name: string;
    label: string;
    type: ReportColumnType;
  }>;
  aggregate?: Aggregate;
  aggregates: Record<string, Record<Aggregate, number>>;
  totalNumberOfRows: number;
  TableComponent?: React.ComponentType;
  getComponentLinkHandler?: GetComponentLinkHandler;
  selectedConfidentialFieldNames: string[];
};

const TableWidget = ({
  data,
  tableFields,
  scopeData,
  aggregate,
  aggregates,
  totalNumberOfRows,
  userLocale,
  i18nLocale,
  hasComponentReadPermission,
  getComponentLinkHandler,
  TableComponent = FixedTable, // use Table for metrics mode
  selectedConfidentialFieldNames,
}: TableWidgetProps) => {
  return (
    <TableWrapper>
      <SearchResultsTable
        withPopover
        isWidget
        variant="light"
        style={{ overflow: 'auto' }}
        components={{
          Thead,
          Table: TableComponent,
          Td: LightTd,
          Th: LightTh,
          BodyTr: LightBodyTr,
        }}
        users={toUsersDataForRenderersById(scopeData?.users ?? [])}
        searchResults={{
          columns: tableFields.map(item => ({
            ...item,
            key: item.name,
            label: item.label,
            sort: null,
          })),
          results: data || [],
          scopeData,
        }}
        aggregatesResultsState={{
          aggregateResults: {
            aggregates: aggregates || null,
            totalNumberOfRows: data?.length || 0,
          },
          aggregateError: null,
        }}
        builderSelectedAggregate={aggregate || null}
        currentUserLocale={userLocale}
        isInteractive
        showAggregate={Boolean(aggregate)}
        setSort={noop}
        hasComponentReadPermission={hasComponentReadPermission}
        getComponentLinkHandler={getComponentLinkHandler}
        selectedConfidentialFieldNames={selectedConfidentialFieldNames}
        labels={{ aggregateLabels: getAggregateLabels(i18nLocale) }}
      />
      <TotalNumberOfRows
        data-tooltip-text={`Showing ${data?.length ?? 0} of ${totalNumberOfRows} ${pluralize('result', totalNumberOfRows)}`}
      >
        {data?.length ?? 0} / {totalNumberOfRows}
      </TotalNumberOfRows>
    </TableWrapper>
  );
};

export { TableWidget };
